import store from "../store";
import authSession from "./authSession";
import User from "./User";
import {
  sessionDataReady,
  SESSION_DATA_LOADING,
  SESSION_DATA_FAILED,
} from "../store/actions/authSessionAction";
import { setCompanyData, updateUserPhone } from "../store/actions/userAction";

const isDev = process.env.NODE_ENV === "development";

export const createUserSessionData = ({ session, authStore, userApi }) => {
  let loadVersion = 0;
  let companyVersion = 0;
  const isCurrent = (sessionId) => !!sessionId && session.syncFromStorage().sessionId === sessionId;

  const load = async ({
    expectedSessionId = session.syncFromStorage().sessionId,
    isActive = () => true,
  } = {}) => {
    if (!isCurrent(expectedSessionId) || !isActive()) return null;
    const version = ++loadVersion;
    const current = () => isCurrent(expectedSessionId) && version === loadVersion && isActive();
    const meta = { sessionId: expectedSessionId };
    const bootstrap = authStore.getState().authSession.status !== "ready";
    if (bootstrap) authStore.dispatch({ type: SESSION_DATA_LOADING, meta });
    try {
      const { data: user } = await userApi.get({ _authSessionId: expectedSessionId });
      if (!current()) return null;
      if (!user?.id) throw new Error("Invalid user response");
      const company = user.companyId
        ? (await userApi.getUsersByCompany(user.companyId, { _authSessionId: expectedSessionId })).data
        : null;
      if (!current()) return null;
      if (user.companyId && company == null) throw new Error("Invalid company response");
      ++companyVersion;
      authStore.dispatch(sessionDataReady(expectedSessionId, user, company));
      if (isDev) {
        console.log("[userSessionData] User.get saved to store", user);
        console.log("[userSessionData] setCompanyData saved to store", company);
      }
      return user;
    } catch (error) {
      if (!current()) return null;
      if (bootstrap)
        authStore.dispatch({ type: SESSION_DATA_FAILED, payload: { code: "profileLoadFailed" }, meta });
      throw error;
    }
  };

  const loadCompany = async (companyId, expectedSessionId) => {
    if (!isCurrent(expectedSessionId)) return null;
    const user = authStore.getState().user;
    const target = companyId || user.companyId;
    if (!target || String(target) !== String(user.companyId)) return null;
    const version = ++companyVersion;
    const { data } = await userApi.getUsersByCompany(target, { _authSessionId: expectedSessionId });
    if (!isCurrent(expectedSessionId) || version !== companyVersion) return null;
    const latest = authStore.getState().user;
    if (latest.id !== user.id || latest.companyId !== user.companyId) return null;
    authStore.dispatch(setCompanyData(data, { sessionId: expectedSessionId }));
    if (isDev) console.log("[userSessionData] setCompanyData saved to store", data);
    return data;
  };
  const updatePhone = async ({ phoneNumber, expectedSessionId, expectedUserId }) => {
    const current = () => isCurrent(expectedSessionId) && authStore.getState().user.id === expectedUserId;
    if (!current()) return null;
    const user = authStore.getState().user;
    // Match the self-profile update contract without submitting company or permissions.
    const response = await userApi.update({
      name: user.name,
      surname: user.surname,
      phoneNumber,
    }, { _authSessionId: expectedSessionId });
    if (!current()) return null;
    const savedPhone = response.data?.phoneNumber?.trim() || phoneNumber;
    // An already pending profile read must not restore the old phone after this write.
    ++loadVersion;
    authStore.dispatch(updateUserPhone(expectedUserId, savedPhone, expectedSessionId));
    return savedPhone;
  };

  return { load, loadCompany, updatePhone };
};

export default createUserSessionData({ session: authSession, authStore: store, userApi: User });
